import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

export class DLError extends Error {
  localizedMessages?: { en: string; zh: string };

  setLocalizedErrorMessage(en: string, zh: string) {
    this.localizedMessages = {
      en,
      zh,
    };
  }

  getMessage(language: string) {
    if (this.localizedMessages && this.localizedMessages[language]) {
      return this.localizedMessages[language];
    }
    if (this.message) {
      return this.message;
    }
    return this.name;
  }
}

export const isDLError = (error: Error): error is DLError => {
  return [
    'UserTokenInvalid',
    'UserNotMember',
    'BadGateway',
    'NotFound',
    'InvalidRequest',
    'InternalServerError',
    'ThrottleError',
    'NetworkError',
  ].includes(error.name);
};

export const useErrorMessage = (): ((error: unknown) => string) => {
  const { t, i18n } = useTranslation();
  return useCallback(
    (error: unknown) => {
      if (typeof error === 'string') {
        return error;
      }
      if (error instanceof Error) {
        if (isDLError(error)) {
          return error.getMessage(i18n.language);
        }
        return error.message;
      }
      return t('unknown-error');
    },
    [i18n.language, t]
  );
};

class ClientOutOfDate extends DLError {
  constructor(message = 'client is out of date') {
    super(message);
    this.name = 'ClientOutOfDate';
  }
}

class UserTokenInvalid extends DLError {
  constructor(message = 'user token is invalid') {
    super(message);
    this.name = 'UserTokenInvalid';
  }
}

class UserNotMember extends DLError {
  constructor(message = 'no membership') {
    super(message);
    this.name = 'UserNotMember';
  }
}

class BadGateway extends DLError {
  constructor(message = 'bad gateway') {
    super(message);
    this.name = 'BadGateway';
  }
}

class InvalidRequest extends DLError {
  constructor(message = 'Invalid request') {
    super(message);
    this.name = 'InvalidRequest';
  }
}

class NotFound extends DLError {
  constructor(message = 'not found') {
    super(message);
    this.name = 'NotFound';
  }
}

class InternalServerError extends DLError {
  constructor(message = 'Unknown error') {
    super(message);
    this.name = 'InternalServerError';
  }
}

class ThrottleError extends DLError {
  constructor(message = 'Too many requests') {
    super(message);
    this.name = 'ThrottleError';
  }
}

class NetworkError extends DLError {
  constructor(message = 'Network error') {
    super(message);
    this.setLocalizedErrorMessage(
      'Having issues connecting to DataLouder',
      '連接到 DataLouder 時遇到一些問題'
    );
    this.name = 'NetworkError';
  }
}

/**
 * When the requests fail, the browser doesn't provide useful information (e.g., CORS failed).
 * https://stackoverflow.com/a/71592364/2361752
 * And different browsers behave differently. Here we handle 3 major browsers
 * and convert such error into our localized NetworkError object.
 */
export const mapNetworkError = (error: Error) => {
  if (
    [
      // firefox
      'TypeError: NetworkError when attempting to fetch resource.',
      // chrome
      'TypeError: Failed to fetch',
      // safari
      'TypeError: Load failed',
    ].includes(String(error))
  ) {
    throw new NetworkError();
  }
  throw error;
};

export default {
  ClientOutOfDate,
  UserTokenInvalid,
  UserNotMember,
  BadGateway,
  NotFound,
  InvalidRequest,
  InternalServerError,
  ThrottleError,
  NetworkError,
};
